import type { TabConfig, QuestionConfig } from '../types/api';
import type {
  ReproveConfig,
  ReproveTab,
  ReproveQuestion,
} from '../types/reprove';

/**
 * Convierte una configuración dinámica desde la BD a formato estático
 * para compatibilidad con el código existente
 */
export function adaptDynamicConfig(dynamicTabs: TabConfig[]): ReproveConfig {
  // Filtrar solo tabs activos y ordenar por index
  const activeTabs = dynamicTabs
    .filter((tab) => tab.active)
    .sort((a, b) => a.index - b.index);

  const adaptedTabs: ReproveTab[] = activeTabs.map((tab) => {
    // Ordenar preguntas por order y adaptarlas
    const adaptedQuestions: ReproveQuestion[] = tab.questions
      .sort((a, b) => a.order - b.order)
      .map((question) => ({
        id: question.id,
        text: question.text,
        points: question.points,
      }));

    return {
      index: tab.index,
      title: tab.title,
      weightPct: tab.weightPct,
      questions: adaptedQuestions,
    };
  });

  return {
    tabs: adaptedTabs,
  };
}

/**
 * Valida que la configuración dinámica sea válida para uso
 */
export function validateDynamicConfig(dynamicTabs: TabConfig[]): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Verificar que hay al menos un tab
  if (!dynamicTabs || dynamicTabs.length === 0) {
    errors.push('No hay tabs configurados');
    return { isValid: false, errors, warnings };
  }

  // Filtrar tabs activos
  const activeTabs = dynamicTabs.filter((tab) => tab.active);

  if (activeTabs.length === 0) {
    errors.push('No hay tabs activos');
    return { isValid: false, errors, warnings };
  }

  // Verificar índices únicos y secuenciales
  const indices = activeTabs.map((tab) => tab.index).sort((a, b) => a - b);
  const expectedIndices = Array.from(
    { length: indices.length },
    (_, i) => i + 1
  );

  if (JSON.stringify(indices) !== JSON.stringify(expectedIndices)) {
    errors.push('Los índices de tabs deben ser secuenciales desde 1');
  }

  // Verificar que los pesos suman 100%
  const totalWeight = activeTabs.reduce((sum, tab) => sum + tab.weightPct, 0);

  if (Math.abs(totalWeight - 100) > 0.01) {
    errors.push(
      `Los pesos de los tabs deben sumar 100% (actual: ${totalWeight.toFixed(
        2
      )}%)`
    );
  }

  // Verificar que cada tab tiene preguntas
  activeTabs.forEach((tab) => {
    if (!tab.questions || tab.questions.length === 0) {
      warnings.push(`El tab "${tab.title}" no tiene preguntas configuradas`);
    }

    // Verificar orden de preguntas
    const orders = tab.questions.map((q) => q.order).sort((a, b) => a - b);
    const expectedOrders = Array.from(
      { length: orders.length },
      (_, i) => i + 1
    );

    if (JSON.stringify(orders) !== JSON.stringify(expectedOrders)) {
      warnings.push(`El orden de preguntas en "${tab.title}" no es secuencial`);
    }

    // Verificar que las preguntas tienen puntos > 0
    tab.questions.forEach((question) => {
      if (question.points <= 0) {
        warnings.push(
          `La pregunta "${question.text}" en "${tab.title}" tiene puntos <= 0`
        );
      }
    });
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * ✅ MEJORADO: Configuración de prueba más realista con pesos que suman 100%
 */
export function createTestConfig(): ReproveConfig {
  return {
    tabs: [
      {
        index: 1,
        title: 'Planeación',
        weightPct: 15,
        questions: [
          { id: 'test-p1', text: '¿Se realizó planeación en Zoho?', points: 5 },
          {
            id: 'test-p2',
            text: '¿Se verificaron los SKUs de productos?',
            points: 5,
          },
          {
            id: 'test-p3',
            text: '¿Se preparó material promocional?',
            points: 5,
          },
          {
            id: 'test-p4',
            text: '¿Se portó credencial de la empresa?',
            points: 5,
          },
          {
            id: 'test-p5',
            text: '¿Se dio de alta el time tracker?',
            points: 5,
          },
          { id: 'test-p6', text: '¿Se llevó catálogo de producto?', points: 5 },
        ],
      },
      {
        index: 2,
        title: 'Acercamiento',
        weightPct: 15,
        questions: [
          { id: 'test-a1', text: '¿Se realizó un saludo cordial?', points: 5 },
          { id: 'test-a2', text: '¿Se presentó correctamente?', points: 5 },
          { id: 'test-a3', text: '¿Mostró actitud profesional?', points: 5 },
        ],
      },
      {
        index: 3,
        title: 'Análisis en PDV',
        weightPct: 10,
        questions: [
          { id: 'test-b1', text: '¿Analizó la competencia?', points: 5 },
          { id: 'test-b2', text: '¿Identificó oportunidades?', points: 5 },
        ],
      },
      {
        index: 4,
        title: 'Verificación de condiciones',
        weightPct: 16,
        questions: [
          {
            id: 'test-c1',
            text: '¿Verificó inventario disponible?',
            points: 5,
          },
          { id: 'test-c2', text: '¿Revisó condiciones de pago?', points: 5 },
          { id: 'test-c3', text: '¿Confirmó espacios disponibles?', points: 6 },
        ],
      },
      {
        index: 5,
        title: 'Detección de oportunidades',
        weightPct: 10,
        questions: [
          {
            id: 'test-d1',
            text: '¿Identificó necesidades del cliente?',
            points: 5,
          },
          { id: 'test-d2', text: '¿Propuso soluciones adecuadas?', points: 5 },
        ],
      },
      {
        index: 6,
        title: 'Capacitación en PDV',
        weightPct: 14,
        questions: [
          { id: 'test-e1', text: '¿Capacitó al personal del PDV?', points: 7 },
          {
            id: 'test-e2',
            text: '¿Explicó beneficios del producto?',
            points: 7,
          },
        ],
      },
      {
        index: 7,
        title: 'Cierre de venta',
        weightPct: 20,
        questions: [
          {
            id: 'test-f1',
            text: '¿Logró cerrar la venta exitosamente?',
            points: 10,
          },
          { id: 'test-f2', text: '¿Acordó términos de entrega?', points: 10 },
        ],
      },
    ],
  };
}

/**
 * Crea una configuración por defecto como fallback de error
 */
export function createDefaultConfig(): ReproveConfig {
  return {
    tabs: [
      {
        index: 1,
        title: 'Error - Configuración no disponible',
        weightPct: 100,
        questions: [
          {
            id: 'default-1',
            text: 'No se pudo cargar la configuración desde la base de datos',
            points: 1,
          },
        ],
      },
    ],
  };
}

/**
 *  Carga configuración con mejor manejo de errores
 */
export async function loadDynamicConfig(): Promise<{
  config: ReproveConfig;
  source: 'dynamic' | 'test' | 'fallback';
  errors?: string[];
  warnings?: string[];
}> {
  try {
    // Importar el servicio dinámicamente para evitar dependencias circulares
    const { wizardService } = await import('../services/wizardService');

    // Cargar configuración desde API
    const dynamicTabs = await wizardService.config.getAllTabs();

    // Validar configuración
    const validation = validateDynamicConfig(dynamicTabs);

    if (!validation.isValid) {
      return {
        config: createTestConfig(),
        source: 'test',
        errors: validation.errors,
        warnings: [
          'Usando configuración de prueba porque la BD no tiene datos válidos',
          ...validation.warnings,
        ],
      };
    }

    // Si hay warnings, log them but continue
    // if (validation.warnings.length > 0) {
    //   console.warn('⚠️ Configuración con warnings:', validation.warnings);
    // }

    // Adaptar y retornar
    const adaptedConfig = adaptDynamicConfig(dynamicTabs);

    return {
      config: adaptedConfig,
      source: 'dynamic',
      warnings: validation.warnings,
    };
  } catch (error) {
    return {
      config: createTestConfig(),
      source: 'test',
      errors: [
        'Error de red o servidor al cargar configuración',
        String(error),
      ],
      warnings: [
        'Usando configuración de prueba por problemas de conectividad',
      ],
    };
  }
}
